import { Dialog } from '@components/dialog';
import { FormControlLabel } from '@components/formControlLabel';
import { RadioButton } from '@components/radioButton';
import { ToastType } from '@components/toast';
import { Box } from '@mui/material';
import { useCreateDocumentGeneration } from '@services/document';
import { useGetPolicy } from '@services/policy';
import { getFormattedPolicyNumber } from '@utils/policy';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { Document, DocumentByTypeId, DocumentType as DocumentTypeLookup } from '@models/document';

type RegenerateTransactionDocumentDialogProps = {
  open: boolean;
  policyId: string;
  document: Document;
  handleCloseDialog: () => void;
};

export const RegenerateTransactionDocumentDialog = ({
  open,
  policyId,
  document,
  handleCloseDialog,
}: RegenerateTransactionDocumentDialogProps) => {
  const isLossHistory = document?.documentType?.id === DocumentTypeLookup.LossHistoryReport.id;
  const hideCreateNewFormSetOption = isLossHistory;

  const [createNewFormSetId, setCreateNewFormSetFields] = useState(hideCreateNewFormSetOption ? false : true);
  const { policy } = useGetPolicy({ policyId });
  const { createDocumentGenerationAsync, isPending: isLoading } = useCreateDocumentGeneration();
  const formattedPolicyNumber = policy
    ? getFormattedPolicyNumber(policy.policyNumberPrefix, policy.policyNumber.policyNumber)
    : '';

  const handleChange = () => {
    setCreateNewFormSetFields((state) => !state);
  };

  function enqueueCannotRegenerateSnack() {
    enqueueSnackbar('Unable to regenerate document', {
      preventDuplicate: true,
      variant: ToastType.Error,
    });
  }

  const { enqueueSnackbar } = useSnackbar();
  const handleSubmit = async () => {
    if (!document.quoteId && !isLossHistory) {
      console.error('QuoteId not found');
      enqueueCannotRegenerateSnack();
      return;
    }
    const packageTypeId = DocumentByTypeId[document.documentType.id]?.packageTypeId ?? undefined;
    if (!packageTypeId) {
      console.error('packageTypeId not found');
      enqueueCannotRegenerateSnack();
      return;
    }
    if (!document.policyId) {
      console.error('policyId not found');
      enqueueCannotRegenerateSnack();
      return;
    }

    await createDocumentGenerationAsync(
      {
        policyId: document.policyId,
        policyTransactionId: document.policyTransactionId ?? undefined,
        quoteId: document.quoteId ?? undefined,
        packageTypeId,
        additionalData: document.additionalData ?? undefined,
        documentFormSetId: createNewFormSetId ? undefined : (document.documentFormSetId ?? undefined),
        override: isLossHistory,
        replacesDocumentId: document.id,
      },
      {
        onSuccess: () => {
          handleCloseDialog();
        },
        onError: () => {
          enqueueSnackbar(`Regenerating transaction document has failed.`, {
            preventDuplicate: true,
            variant: ToastType.Error,
          });
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      title={`Regenerate Transaction Document on ${formattedPolicyNumber}`}
      showBorders={false}
      handleCloseDialog={handleCloseDialog}
      primaryBtnText='Regenerate Document'
      isPrimaryBtnLoading={isLoading}
      primaryBtnAction={handleSubmit}
      secondaryBtnText='Cancel'
      secondaryBtnAction={handleCloseDialog}
      sxProps={{
        dialogContent: { display: 'flex-start' },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', margin: 'auto', padding: '20px' }}>
        {!hideCreateNewFormSetOption && (
          <FormControlLabel
            key='newFormSet'
            checked={createNewFormSetId}
            control={<RadioButton value={createNewFormSetId} onChange={handleChange} />}
            label='Create new document form set'
          />
        )}
        <FormControlLabel
          key='existingFormSet'
          checked={!createNewFormSetId}
          control={<RadioButton value={!createNewFormSetId} onChange={handleChange} />}
          label='Use existing document form set'
        />
      </Box>
    </Dialog>
  );
};
